<template>
  <div class="dialog-overlay" @click.self="$emit('close')">
    <div class="dialog-content">
      <div class="dialog-header">
        <h3 class="dialog-title">Change Status</h3>
        <button class="close-button" @click="$emit('close')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="close-icon">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      <div class="dialog-body">
        <p class="dialog-description">
          Select a new status for the {{ selectedCount }} selected course(s).
        </p>
        
        <div class="status-options">
          <label class="status-option">
            <input type="radio" name="status" value="active" v-model="selectedStatus">
            <div class="status-card">
              <span class="status-dot active"></span>
              <span class="status-label">Active</span>
              <span class="status-description">Make courses available to students</span>
            </div>
          </label>
          
          <label class="status-option">
            <input type="radio" name="status" value="inactive" v-model="selectedStatus">
            <div class="status-card">
              <span class="status-dot inactive"></span>
              <span class="status-label">Inactive</span>
              <span class="status-description">Hide courses from students</span>
            </div>
          </label>
          
          <label class="status-option">
            <input type="radio" name="status" value="draft" v-model="selectedStatus">
            <div class="status-card">
              <span class="status-dot draft"></span>
              <span class="status-label">Draft</span>
              <span class="status-description">Work in progress, not published</span>
            </div>
          </label>
        </div>
      </div>
      
      <div class="dialog-footer">
        <button class="cancel-button" @click="$emit('close')">Cancel</button>
        <button 
          class="confirm-button" 
          :disabled="!selectedStatus" 
          @click="confirmChange"
        >
          Change Status
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChangeStatusDialog',
  props: {
    selectedCount: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      selectedStatus: ''
    };
  },
  methods: {
    confirmChange() {
      if (this.selectedStatus) {
        console.log(`Changing status to: ${this.selectedStatus} for ${this.selectedCount} courses`);
        this.$emit('change', this.selectedStatus);
      }
    }
  }
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
  padding: 1rem;
  overflow-y: auto;
  animation: fade-in 0.2s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-content {
  background-color: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: slide-up 0.3s ease-out;
}

@keyframes slide-up {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #E2E8F0;
  background: linear-gradient(135deg, #F8FAFC, #EEF2FF);
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748B;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #F1F5F9;
  color: #1E293B;
}

.close-icon {
  width: 18px;
  height: 18px;
}

.dialog-body {
  padding: 1.5rem;
}

.dialog-description {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #475569;
  font-size: 0.875rem;
}

.status-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.status-option {
  position: relative;
  cursor: pointer;
}

.status-option input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.status-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  background-color: #F8FAFC;
  transition: all 0.2s;
}

.status-option input:checked + .status-card {
  border-color: #6366F1;
  background-color: #EEF2FF;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-bottom: 0.5rem;
}

.status-dot.active {
  background-color: #059669;
}

.status-dot.inactive {
  background-color: #DC2626;
}

.status-dot.draft {
  background-color: #D97706;
}

.status-label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1E293B;
  margin-bottom: 0.25rem;
}

.status-description {
  font-size: 0.75rem;
  color: #64748B;
}

.dialog-footer {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid #E2E8F0;
  background-color: #F8FAFC;
}

.cancel-button {
  padding: 0.75rem 1.25rem;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  background-color: white;
  color: #64748B;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background-color: #F1F5F9;
  color: #334155;
}

.confirm-button {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  background-color: #6366F1;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-button:hover:not(:disabled) {
  background-color: #4F46E5;
  transform: translateY(-1px);
}

.confirm-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style> 